import { Box, Button, Group, Tooltip } from "@mantine/core";
import {
  CellEditRequestEvent,
  ColDef,
  ColDefField,
  GridOptions,
  RowDragEndEvent,
  SortChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Draft, WritableDraft } from "immer";
import { forwardRef, Ref, useEffect, useImperativeHandle, useRef } from "react";
import { useImmer } from "use-immer";
import { AG_GRID_LOCALE_NOR } from "../../agGridLocale";
import { ValidatorValue, VETColValidator } from "./ViewEditTableValidators";

type TDataWithInnerId<TData> = TData & { ___id: number };

export type ViewEditTableRef<TData> = {
  getData: () => {
    rows: TData[];
    validationErrors: string[];
  };
};

const Ids = {
  ___id: "___id",
  ___deleteRow: "___deleteRow",
  ___dragOrder: "___dragOrder",
} as const;

export type ViewEditTableProps<TData> = {
  gridOptions?: GridOptions<TData>;
  validators?: VETColValidator<TData, ValidatorValue>[];
  columns: ColDef<TData, any>[];
  data: TData[];
  onSave: (data: TData[]) => void;
  onRowsChange?: (rows: TData[]) => void;
  newItemTemplateObject: TData;
  orderProp?: ColDefField<TData>;
  useIndexForDragOrder?: boolean;
  canEdit?: boolean;
  canAddRows?: boolean;
  canRemoveRows?: boolean;
  canRemovePredicate?: (item: TData) => boolean;
  dropdownContainer?: string;
  alwaysInEditMode?: boolean;
};

function ViewEditTable<TData>(
  {
    gridOptions,
    validators,
    columns,
    data,
    onSave,
    onRowsChange,
    newItemTemplateObject,
    orderProp,
    useIndexForDragOrder = false,
    canEdit = false,
    canAddRows = true,
    canRemoveRows = true,
    canRemovePredicate,
    dropdownContainer = "dropdown-container",
    alwaysInEditMode = false,
  }: ViewEditTableProps<TData>,
  ref: Ref<ViewEditTableRef<TData>>,
) {
  const gridRef = useRef<AgGridReact<TDataWithInnerId<TData>>>(null);
  const [editMode, setEditMode] = useImmer<boolean>(false);
  const [columnsInner, setColumnsInner] = useImmer<ColDef<TDataWithInnerId<TData>>[]>(
    columns as ColDef<TDataWithInnerId<TData>>[],
  );
  const [dataInner, setDataInner] = useImmer<TDataWithInnerId<TData>[]>([]);
  const [tempAutoSortOrder, setTempAutoSortOrder] = useImmer<TDataWithInnerId<TData>[]>([]);
  const dragEnabled = orderProp != null || useIndexForDragOrder === true;

  useImperativeHandle(
    ref,
    () => ({
      getData: () => {
        const rows = strip(dataInner);
        const validationErrors = validate(rows);
        return { rows, validationErrors };
      },
    }),
    [dataInner],
  );

  useEffect(() => {
    const _data = data.map((item, index) => ({ ...item, ___id: index }));
    setDataInner(_data);
  }, [data, setDataInner]);

  useEffect(() => {
    setColumnsInner(columns as ColDef<TDataWithInnerId<TData>>[]);
  }, [columns, setColumnsInner]);

  const deleteRowCol: ColDef<TData> = {
    colId: Ids.___deleteRow,
    headerName: "",
    sortable: false,
    cellRenderer: (params) => {
      let showRemoveButton = true;
      if (canRemovePredicate != null) {
        showRemoveButton = canRemovePredicate(params.node.data);
      }

      return (
        showRemoveButton && (
          <Button
            color="red.5"
            variant="subtle"
            size="compact-sm"
            onClick={() => params.node?.rowIndex != null && params.onDelete(params.node.rowIndex)}
          >
            <i className="fa fa-trash fa-fw" />
          </Button>
        )
      );
    },
    width: 50,
    cellClass: "px-1",
    suppressHeaderMenuButton: true,
    suppressSizeToFit: true,
    cellRendererParams: {
      onDelete: (rowIndex: number) => {
        setDataInner((draft) => {
          draft.splice(rowIndex, 1);
        });
      },
    },
  };

  const dragOrderCol: ColDef<TData> = {
    colId: Ids.___dragOrder,
    headerName: "",
    width: 50,
    suppressHeaderMenuButton: true,
    suppressSizeToFit: true,
    rowDrag: true,
    sortable: false,
  };

  useEffect(() => {
    const hasDeleteRowCol = columnsInner.find((col) => col.colId === Ids.___deleteRow) != null;
    const hasDragOrderCol = columnsInner.find((col) => col.colId === Ids.___dragOrder) != null;

    setEditMode(editMode || alwaysInEditMode);

    setColumnsInner((draft) => {
      if (canEdit === true && (editMode === true || alwaysInEditMode === true)) {
        if ((canAddRows === true || canRemoveRows === true) && hasDeleteRowCol === false) {
          draft.splice(0, 0, deleteRowCol as WritableDraft<ColDef<TDataWithInnerId<TData>, any>>);
        }
        if (dragEnabled === true && hasDragOrderCol === false) {
          draft.splice(0, 0, dragOrderCol as WritableDraft<ColDef<TDataWithInnerId<TData>, any>>);
        }
      }

      // hide columns when cellRendererParams.editModeHidden === true in edit mode
      draft.forEach((col) => {
        col.hide = col.cellRendererParams?.editModeHidden === true && (editMode === true || alwaysInEditMode === true);
      });
    });
  }, [
    orderProp,
    canEdit,
    editMode,
    alwaysInEditMode,
    columnsInner,
    setColumnsInner,
    setEditMode,
    canAddRows,
    canRemoveRows,
    dragEnabled,
  ]);

  useEffect(() => {
    if (!gridRef.current?.api) return;

    gridRef.current.api.setGridOption(
      "suppressCellFocus",
      canEdit === false || (editMode === false && alwaysInEditMode === false),
    );
    gridRef.current.api.setGridOption(
      "suppressClickEdit",
      canEdit === false || (editMode === false && alwaysInEditMode === false),
    );
  }, [canEdit, editMode, alwaysInEditMode]);

  const onCellEditRequest = (event: CellEditRequestEvent<TDataWithInnerId<TData>>) => {
    setDataInner((draft) => {
      const index = draft.findIndex((item) => item.___id === event.data.___id);
      if (index === -1) {
        console.warn("Row not found for edit request:", event.data);
        return;
      }
      const draftRow = draft[index];
      const fieldParts = event.colDef.field.split(".");
      const lastField = fieldParts.pop();
      const field = fieldParts.reduce((acc, curr) => acc[curr], draftRow as any);
      (field as any)[lastField as string] = event.newValue;
    });
  };

  const abort = () => {
    setDataInner(data.map((item, index) => ({ ...item, ___id: index })));
    setColumnsInner(columns as ColDef<TDataWithInnerId<TData>>[]);
    setEditMode(false);
  };

  const strip = (dataArr: TDataWithInnerId<TData>[]): TData[] =>
    dataArr.map((item) => {
      const i: any = { ...item };
      delete i["___id"];
      return i as TData;
    });

  // Notify parent of row changes (edits, add, delete, drag reorder)
  useEffect(() => {
    if (onRowsChange) {
      onRowsChange(strip(dataInner));
    }
  }, [dataInner, onRowsChange]);

  const validate = (rows: TData[]): string[] => {
    let errors: string[] = [];
    if (validators?.length > 0) {
      errors = columns.reduce((acc, col, colIndex) => {
        if (col.field != null) {
          const validator = validators[colIndex];
          if (validator != null) {
            rows.forEach((row, rowIndex) => {
              const value = (row as any)[col.field as string];
              if (validator.validate(value, rows, rowIndex, colIndex) === false) {
                acc.push(
                  `Rad ${rowIndex + 1}, kolonne ${colIndex + 1} er ugyldig.\nVerdi: \"${value ?? ""}\"\nFeil: ${
                    validator.errorMessage
                  }`,
                );
              }
            });
          }
        }
        return acc;
      }, [] as string[]);
    }
    return errors;
  };

  const onSaveChanges = () => {
    const rows = strip(dataInner);
    const validationErrors = validate(rows);
    if (validationErrors.length > 0) {
      alert(validationErrors.join("\n"));
      return;
    }
    onSave(rows);
    setColumnsInner(columns as ColDef<TDataWithInnerId<TData>>[]);
    setEditMode(false);
  };

  const applySortOrder = () => {
    setDataInner(tempAutoSortOrder);
    setTempAutoSortOrder([]);
    gridRef?.current?.api.resetColumnState();
  };

  const onSortChanged = (event: SortChangedEvent<TDataWithInnerId<TData>>) => {
    const sortColumn = event.api.getColumnState().filter((s) => s.sort !== null)[0];
    if (dragEnabled === true && sortColumn?.sort != null) {
      const updatedData: TDataWithInnerId<TData>[] = [];
      event.api.forEachNodeAfterFilterAndSort((rowNode, index) => {
        updatedData.push({ ...rowNode.data, [orderProp as string]: index } as any);
      });
      setTempAutoSortOrder(updatedData);
    } else {
      setTempAutoSortOrder([]);
    }
  };

  const rowDragEnd = (event: RowDragEndEvent<TDataWithInnerId<TData>>) => {
    const updatedData: TDataWithInnerId<TData>[] = [];
    event.api.forEachNode((node, index) => {
      updatedData.push({ ...node.data, [orderProp as string]: index } as any);
    });
    setDataInner(updatedData);
  };

  const addRow = () => {
    setDataInner((draft) => {
      const randomId = Math.floor(Math.random() * 1000000);
      draft.push({ ...(newItemTemplateObject as any), ___id: randomId } as Draft<TDataWithInnerId<TData>>);
    });
  };

  return (
    <Box className="ag-theme-alpine" style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <AgGridReact
        suppressMovableColumns
        domLayout="autoHeight"
        localeText={AG_GRID_LOCALE_NOR}
        ref={gridRef}
        getRowId={(e) => String(e.data.___id)}
        defaultColDef={{ suppressHeaderMenuButton: true }}
        onCellEditRequest={onCellEditRequest}
        readOnlyEdit
        stopEditingWhenCellsLoseFocus
        onSortChanged={onSortChanged}
        suppressContextMenu
        suppressCellFocus
        suppressClickEdit
        enterNavigatesVerticallyAfterEdit
        columnDefs={columnsInner}
        rowData={dataInner}
        animateRows
        rowDragManaged={dragEnabled && canEdit === true && (editMode === true || alwaysInEditMode === true)}
        onRowDragEnd={(event) => rowDragEnd(event)}
        popupParent={document.getElementById(dropdownContainer) || undefined}
        {...(gridOptions as GridOptions<TDataWithInnerId<TData>>)}
      />

      {canEdit === true ? (
        <Group mt="sm" gap="xs">
          {editMode === true || alwaysInEditMode === true ? (
            <>
              {canAddRows === true && (
                <Button px="sm" color="green" onClick={addRow}>
                  <i className="fa fa-plus fa-fw" />
                </Button>
              )}

              {dragEnabled === true && tempAutoSortOrder.length > 0 && (
                <>
                  <Tooltip
                    label="Overskriver egendefinert rekkefølge med automatisk sortering."
                    withArrow
                    openDelay={200}
                  >
                    <Button color="green" onClick={applySortOrder}>
                      Bruk rekkefølgen
                    </Button>
                  </Tooltip>
                </>
              )}

              {alwaysInEditMode === false && (
                <>
                  <Button px="sm" onClick={onSaveChanges} leftSection={<i className="fa fa-save" />}>
                    Lagre
                  </Button>
                  <Button
                    px="sm"
                    variant="outline"
                    onClick={abort}
                    color="warmgray.7"
                    leftSection={<i className="fa fa-times" />}
                  >
                    Avbryt
                  </Button>
                </>
              )}
            </>
          ) : (
            alwaysInEditMode === false && (
              <Button
                px="sm"
                variant="outline"
                leftSection={<i className="fa fa-pencil" />}
                onClick={() => setEditMode(true)}
              >
                Rediger
              </Button>
            )
          )}
        </Group>
      ) : null}
    </Box>
  );
}

export default forwardRef(ViewEditTable) as <T>(
  props: ViewEditTableProps<T> & { ref?: Ref<ViewEditTableRef<T>> },
) => ReturnType<typeof ViewEditTable>;
